import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import {
  Calendar,
  CheckCircle2,
  Flame,
  Lightbulb,
  Lock,
  Sparkles,
  Star,
  Trophy,
  UserRound,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { db } from '../../../firebase/config';
import { useAuth } from '../../../hooks/useAuth';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Input } from '../../ui/input';
import coloradoImage from '../../../colorado.webp';
import DailyLeaderboard from './DailyLeaderboard';
import GlobalLeaderboard from './GlobalLeaderboard';
import LetterGrid from './LetterGrid';
import SubmitMapDialog from './SubmitMapDialog';

const MAX_ATTEMPTS = 6;
const HINT_COST = 15;
const ATTEMPT_PENALTY = 10;
const BASE_POINTS = 100;

const HINT_DEFS = [
  { key: 'tier', label: 'Tier', icon: Flame, blurb: 'Reveal the map tier.' },
  { key: 'mapper', label: 'Mapper', icon: UserRound, blurb: 'Who built the map.' },
  { key: 'letter', label: 'Free letter', icon: Sparkles, blurb: 'Flip one random letter.' },
];

const BERLIN_DATE_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const todayStr = () => BERLIN_DATE_FMT.format(new Date());

const scoreFor = ({ won, attempts, hintsUsed }) => {
  if (!won) return 0;
  const raw = BASE_POINTS - ATTEMPT_PENALTY * (attempts - 1) - HINT_COST * hintsUsed;
  return Math.max(10, raw);
};

const DailyMapGame = () => {
  const { currentUser, userProfile } = useAuth();

  const [loading, setLoading] = useState(true);
  const [challenge, setChallenge] = useState(null);
  const [error, setError] = useState(null);

  const [attempts, setAttempts] = useState([]);
  const [currentGuess, setCurrentGuess] = useState('');
  const [won, setWon] = useState(false);
  const [lost, setLost] = useState(false);
  const [hintsUsed, setHintsUsed] = useState({});
  const [revealedIdx, setRevealedIdx] = useState(() => new Set());
  const [animatingRow, setAnimatingRow] = useState(null);
  const [alreadyPlayed, setAlreadyPlayed] = useState(false);
  const [savedResult, setSavedResult] = useState(null);
  const startedAt = useRef(Date.now());

  const target = challenge?.mapImage?.mapName || '';
  const targetUpper = target.toUpperCase();
  const letterCount = useMemo(() => targetUpper.replace(/\s+/g, '').length, [targetUpper]);

  // ─── Load today's challenge ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);

        const params = new URLSearchParams(window.location.search);
        const previewName = params.get('preview');
        if (previewName) {
          if (cancelled) return;
          setChallenge({
            id: 'preview',
            date: todayStr(),
            mapImageId: 'preview',
            mapImage: {
              id: 'preview',
              mapName: previewName,
              imageUrl: coloradoImage,
              mapper: 'Preview mapper',
              tier: 5,
              difficulty: 3,
              hintText: 'Preview mode — no result will be saved.',
            },
          });
          return;
        }

        // The current map is whichever dailyChallenges doc has the latest date.
        // n8n writes a new one each night; if it fails, the previous one stays
        // current — which is what we want.
        const latestQ = query(collection(db, 'dailyChallenges'), orderBy('date', 'desc'), limit(1));
        const latestSnap = await getDocs(latestQ);
        if (latestSnap.empty) {
          throw new Error('No map has been published yet.');
        }
        const challengeDoc = latestSnap.docs[0];
        const data = challengeDoc.data();
        const mapSnap = await getDoc(doc(db, 'mapImages', data.mapImageId));
        if (!mapSnap.exists()) throw new Error('Map image missing from library.');

        if (cancelled) return;
        setChallenge({
          id: challengeDoc.id,
          ...data,
          mapImage: { id: mapSnap.id, ...mapSnap.data() },
        });

        if (currentUser) {
          const attemptsQ = query(
            collection(db, 'dailyChallengeAttempts'),
            where('challengeId', '==', challengeDoc.id),
            where('userId', '==', currentUser.uid),
            limit(1)
          );
          const prev = await getDocs(attemptsQ);
          if (!prev.empty) {
            const r = { id: prev.docs[0].id, ...prev.docs[0].data() };
            setAlreadyPlayed(true);
            setSavedResult(r);
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  // ─── Persistence ───────────────────────────────────────────────────────────
  const persistResult = useCallback(
    async ({ isWin, attemptCount, hintCount, points }) => {
      if (!currentUser || !challenge || challenge.id === 'preview') return;
      const username = userProfile?.username || 'Anonymous';
      const timeSeconds = Math.round((Date.now() - startedAt.current) / 1000);
      try {
        await addDoc(collection(db, 'dailyChallengeAttempts'), {
          challengeId: challenge.id,
          userId: currentUser.uid,
          username,
          guessedMap: isWin ? targetUpper : attempts[attempts.length - 1] || '',
          isCorrect: isWin,
          isClose: false,
          points,
          attempts: attemptCount,
          hintsUsed: hintCount,
          timeSeconds,
          timestamp: serverTimestamp(),
        });

        const scoreRef = doc(db, 'userDailyChallengeScores', currentUser.uid);
        const scoreSnap = await getDoc(scoreRef);
        if (scoreSnap.exists()) {
          const prev = scoreSnap.data();
          await updateDoc(scoreRef, {
            totalPoints: (prev.totalPoints || 0) + points,
            totalChallenges: (prev.totalChallenges || 0) + 1,
            correctGuesses: (prev.correctGuesses || 0) + (isWin ? 1 : 0),
            currentStreak: isWin ? (prev.currentStreak || 0) + 1 : 0,
            bestStreak: Math.max(prev.bestStreak || 0, isWin ? (prev.currentStreak || 0) + 1 : 0),
            lastPlayed: serverTimestamp(),
            username,
          });
        } else {
          await setDoc(scoreRef, {
            userId: currentUser.uid,
            username,
            totalPoints: points,
            totalChallenges: 1,
            correctGuesses: isWin ? 1 : 0,
            currentStreak: isWin ? 1 : 0,
            bestStreak: isWin ? 1 : 0,
            lastPlayed: serverTimestamp(),
          });
        }
      } catch (e) {
        toast.error('Could not save your result.', { description: e.message });
      }
    },
    [attempts, challenge, currentUser, targetUpper, userProfile]
  );

  // ─── Gameplay ──────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const raw = e.target.value
      .toUpperCase()
      .replace(/[^A-Z0-9_]/g, '')
      .slice(0, letterCount);
    setCurrentGuess(raw);
  };

  const submitGuess = (e) => {
    e?.preventDefault?.();
    if (alreadyPlayed || won || lost) return;
    if (currentGuess.length !== letterCount) {
      toast.error(`Use ${letterCount} letters.`);
      return;
    }
    const nextAttempts = [...attempts, currentGuess];
    setAttempts(nextAttempts);
    setAnimatingRow(nextAttempts.length - 1);
    setCurrentGuess('');

    const isWin = currentGuess === targetUpper.replace(/\s+/g, '');
    const flipDuration = letterCount * 80 + 600;

    if (isWin) {
      setTimeout(() => setWon(true), flipDuration);
      const hintCount = Object.keys(hintsUsed).length;
      const points = scoreFor({
        won: true,
        attempts: nextAttempts.length,
        hintsUsed: hintCount,
      });
      persistResult({
        isWin: true,
        attemptCount: nextAttempts.length,
        hintCount,
        points,
      });
    } else if (nextAttempts.length >= MAX_ATTEMPTS) {
      setTimeout(() => setLost(true), flipDuration);
      persistResult({
        isWin: false,
        attemptCount: nextAttempts.length,
        hintCount: Object.keys(hintsUsed).length,
        points: 0,
      });
    }
  };

  const unlockHint = (key) => {
    if (alreadyPlayed || won || lost) return;
    if (hintsUsed[key]) return;

    if (key === 'letter') {
      const clean = targetUpper
        .split('')
        .map((c, i) => ({ c, i }))
        .filter(({ c, i }) => c !== ' ' && !revealedIdx.has(i));
      if (!clean.length) return;
      const pick = clean[Math.floor(Math.random() * clean.length)];
      const next = new Set(revealedIdx);
      next.add(pick.i);
      setRevealedIdx(next);
    }
    setHintsUsed((h) => ({ ...h, [key]: true }));
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-muted-foreground">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-border border-t-heat" />
          <p className="font-mono text-xs uppercase tracking-[0.3em]">Loading today's drop…</p>
        </div>
      </div>
    );
  }

  if (error || !challenge) {
    return (
      <div className="mx-auto max-w-lg rounded-lg border border-border/60 bg-card p-8 text-center">
        <h2 className="font-display text-xl font-semibold">No daily map available</h2>
        <p className="mt-3 text-sm text-muted-foreground">
          {error || 'Check back later — admin needs to add images to the pool.'}
        </p>
      </div>
    );
  }

  const img = challenge.mapImage;
  const hintCount = Object.keys(hintsUsed).length;
  const showResult = alreadyPlayed || won || lost;
  const finalResult = savedResult || {
    isCorrect: won,
    attempts: attempts.length,
    hintsUsed: hintCount,
    points: won ? scoreFor({ won: true, attempts: attempts.length, hintsUsed: hintCount }) : 0,
  };

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-8 sm:gap-8 sm:px-6 sm:py-10">
      <Header letterCount={letterCount} difficulty={img.difficulty} />

      <MapSurface image={img} reveal={showResult} />

      <LetterGrid target={targetUpper} attempts={attempts} animatingRow={animatingRow} won={won} />

      {!showResult && (
        <form onSubmit={submitGuess} className="mx-auto flex w-full max-w-md flex-col gap-3">
          <Input
            autoFocus
            value={currentGuess}
            onChange={handleChange}
            placeholder={`${letterCount} characters`}
            className="h-11 text-center font-mono text-lg uppercase tracking-[0.25em]"
            maxLength={letterCount}
            inputMode="text"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="characters"
            spellCheck={false}
          />
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
              {MAX_ATTEMPTS - attempts.length} guesses left
            </span>
            <Button
              type="submit"
              disabled={currentGuess.length !== letterCount}
              className="font-mono uppercase tracking-[0.2em]"
            >
              Submit guess
            </Button>
          </div>
        </form>
      )}

      <HintPanel
        hints={hintsUsed}
        onUnlock={unlockHint}
        locked={showResult}
        mapper={img.mapper}
        tier={img.tier}
        revealedIdx={revealedIdx}
        target={targetUpper}
      />

      {showResult && <ResultCard result={finalResult} target={targetUpper} />}

      {showResult && challenge.id !== 'preview' && (
        <DailyLeaderboard challengeId={challenge.id} currentUserId={currentUser?.uid} />
      )}

      {challenge.id !== 'preview' && <GlobalLeaderboard currentUserId={currentUser?.uid} />}
    </div>
  );
};

// ─── Header ──────────────────────────────────────────────────────────────────
const Header = ({ letterCount, difficulty }) => {
  const date = new Date().toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const level = Math.max(1, Math.min(5, difficulty || 1));
  return (
    <header className="flex flex-col gap-3 border-b border-border/60 pb-6">
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
        <span className="h-px w-6 bg-heat" />
        <Calendar className="h-3 w-3" />
        {date} · Map of the day
      </div>
      <div className="flex items-end justify-between gap-4">
        <h1 className="font-display text-3xl font-bold leading-none tracking-tight sm:text-4xl">
          Name the map.
        </h1>
        <div className="flex flex-col items-end gap-1">
          <span className="inline-flex items-center gap-1" title={`Guess difficulty ${level}/5`}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={i}
                className={cn(
                  'h-3.5 w-3.5',
                  i < level ? 'fill-heat text-heat' : 'text-muted-foreground/40'
                )}
              />
            ))}
          </span>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
            {letterCount} · letters
          </span>
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 pt-1">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
          Got a map we're missing?
        </span>
        <SubmitMapDialog />
      </div>
    </header>
  );
};

// ─── Map surface ─────────────────────────────────────────────────────────────
const MapSurface = ({ image, reveal }) => (
  <div className="relative overflow-hidden rounded-xl border border-border/60 bg-muted/20">
    <div className="aspect-[4/3] w-full">
      <img
        src={image.imageUrl}
        alt=""
        draggable={false}
        className={cn(
          'h-full w-full object-cover transition-all duration-500',
          !reveal && 'saturate-[0.6]'
        )}
      />
    </div>
    {image.hintText && (
      <div className="absolute inset-x-0 bottom-0 border-t border-border/40 bg-background/85 px-4 py-3 text-xs text-muted-foreground backdrop-blur-sm">
        <span className="font-mono uppercase tracking-[0.2em] text-heat">Field note · </span>
        {image.hintText}
      </div>
    )}
  </div>
);

// ─── Hints ───────────────────────────────────────────────────────────────────
const HintPanel = ({ hints, onUnlock, locked, mapper, tier, revealedIdx, target }) => {
  const used = Object.keys(hints).length;
  return (
    <Card className="border-border/60 bg-background/40">
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            <Lightbulb className="h-3 w-3 text-heat" />
            Hints · {used}/3
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            −{HINT_COST} pts each
          </span>
        </div>

        <div className="grid gap-2 sm:grid-cols-3">
          {HINT_DEFS.map(({ key, label, icon: Icon, blurb }) => {
            const unlocked = hints[key];
            const disabled = locked || unlocked;
            let revealedValue = null;
            if (unlocked && key === 'mapper') revealedValue = mapper || 'Unknown mapper';
            if (unlocked && key === 'tier') revealedValue = `Tier ${tier || '?'}`;
            if (unlocked && key === 'letter') {
              const parts = [];
              if (revealedIdx && target) {
                [...revealedIdx]
                  .sort((a, b) => a - b)
                  .forEach((i) => {
                    parts.push(`#${i + 1}: ${target[i]}`);
                  });
              }
              revealedValue = parts.length ? parts.join(' · ') : 'Letter flipped';
            }

            return (
              <button
                key={key}
                type="button"
                disabled={disabled && !unlocked}
                onClick={() => onUnlock(key)}
                className={cn(
                  'group flex min-h-[96px] flex-col items-start gap-1 rounded-md border p-3 text-left transition-colors',
                  unlocked
                    ? 'border-heat/50 bg-heat/10'
                    : locked
                      ? 'border-border/50 opacity-60'
                      : 'border-border/60 hover:border-heat/60 hover:bg-heat/5'
                )}
              >
                <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  {unlocked ? <Icon className="h-3 w-3 text-heat" /> : <Lock className="h-3 w-3" />}
                  {label}
                </span>
                {unlocked ? (
                  <span className="text-sm font-medium text-foreground">{revealedValue}</span>
                ) : (
                  <span className="text-xs text-muted-foreground">{blurb}</span>
                )}
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Result ──────────────────────────────────────────────────────────────────
const ResultCard = ({ result, target }) => {
  const isWin = result.isCorrect;
  return (
    <Card
      className={cn(
        'border-border/60',
        isWin ? 'bg-[hsl(var(--winner))]/10' : 'bg-[hsl(var(--destructive))]/10'
      )}
    >
      <CardContent className="flex flex-col gap-3 p-5">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
          {isWin ? (
            <CheckCircle2 className="h-4 w-4 text-[hsl(var(--winner))]" />
          ) : (
            <XCircle className="h-4 w-4 text-destructive" />
          )}
          {isWin ? 'Solved' : 'Not today'}
        </div>
        <h2 className="font-display text-2xl font-semibold">{target}</h2>
        <div className="grid grid-cols-3 gap-4 border-t border-border/60 pt-3">
          <Stat label="Points" value={result.points ?? 0} accent={isWin} />
          <Stat label="Attempts" value={result.attempts ?? '—'} />
          <Stat label="Hints" value={result.hintsUsed ?? 0} />
        </div>
        <div className="flex items-center gap-2 pt-1 text-xs text-muted-foreground">
          <Trophy className="h-3.5 w-3.5 text-heat" />
          <span>New map drops at midnight. Come back tomorrow.</span>
        </div>
      </CardContent>
    </Card>
  );
};

const Stat = ({ label, value, accent }) => (
  <div className="flex flex-col gap-1">
    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
      {label}
    </span>
    <span
      className={cn(
        'font-display text-2xl font-semibold tabular-nums',
        accent && 'text-[hsl(var(--winner))]'
      )}
    >
      {value}
    </span>
  </div>
);

export default DailyMapGame;
